import type { Graph, Edge, Node } from '@antv/x6';

type EdgeRoutingMeta = {
  _mergedEdge?: boolean;
  _originalConnector?: any;
  _originalRouter?: any;
  edgeSubtype?: string;
  edgeCategory?: string;
  isOptionArrowEdge?: boolean;
};

type EdgeDirection = 'top' | 'bottom' | 'left' | 'right';

const OPTION_ARROW_ELBOW = 26;
const OPTION_ARROW_APPROACH = 12;
const OPTION_ARROW_VERTICAL_GAP = 0;
const DEFAULT_BOTTOM_DROP = 24;
const DEFAULT_BOTTOM_APPROACH = 12;

const getNodeCenter = (node: Node) => {
  const pos = node.getPosition();
  const size = node.getSize();
  return {
    x: pos.x + size.width / 2,
    y: pos.y + size.height / 2,
  };
};

const pickDirectionFacingSource = (): EdgeDirection => 'top';

const buildBottomEntryVertices = (
  graph: Graph,
  edge: Edge,
  sourceNode: Node,
  targetNode: Node,
) => {
  const sourceBottom = getBoundaryPoint(sourceNode, 'bottom');
  const drop = shiftPoint(sourceBottom, 'bottom', DEFAULT_BOTTOM_DROP);
  const vertices = [drop];
  const targetBottom = getBoundaryPoint(targetNode, 'bottom');
  const targetApproach = shiftPoint(targetBottom, 'bottom', DEFAULT_BOTTOM_APPROACH);
  if (Math.abs(targetApproach.x - drop.x) > 1) {
    vertices.push({ x: targetApproach.x, y: drop.y });
  }
  if (Math.abs(targetApproach.y - drop.y) > 1) {
    vertices.push({ x: targetApproach.x, y: targetApproach.y });
  }
  return vertices;
};

const getBoundaryPoint = (node: Node, dir: EdgeDirection) => {
  const pos = node.getPosition();
  const size = node.getSize();
  const cx = pos.x + size.width / 2;
  const cy = pos.y + size.height / 2;
  switch (dir) {
    case 'top':
      return { x: cx, y: pos.y };
    case 'bottom':
      return { x: cx, y: pos.y + size.height };
    case 'left':
      return { x: pos.x, y: cy };
    case 'right':
    default:
      return { x: pos.x + size.width, y: cy };
  }
};

export const buildOptionArrowVertices = (
  graph: Graph,
  edge: Edge,
  targetApproachDir?: EdgeDirection,
) => {
  if (!isOptionArrowEdge(edge)) return null;
  const sourceId = edge.getSourceCellId?.();
  const targetId = edge.getTargetCellId?.();
  if (!sourceId || !targetId) return null;
  const sourceNode = graph.getCellById(sourceId) as Node | null;
  const targetNode = graph.getCellById(targetId) as Node | null;
  if (!sourceNode || !targetNode || !sourceNode.isNode?.() || !targetNode.isNode?.()) return null;
  const boundaryPoint = getBoundaryPoint(sourceNode, 'top');
  const elbow = shiftPoint(boundaryPoint, 'top', OPTION_ARROW_ELBOW);
  const direction = targetApproachDir || pickDirectionFacingSource();
  const targetBoundary = getBoundaryPoint(targetNode, direction);
  const arrowStartPoint = shiftPoint(targetBoundary, direction, OPTION_ARROW_APPROACH);
  const targetX = arrowStartPoint.x;
  const targetY = arrowStartPoint.y;
  const vertices = [elbow];
  if (Math.abs(targetX - elbow.x) > 1) {
    vertices.push({ x: targetX, y: elbow.y });
  }
  const last = vertices[vertices.length - 1];
  if (Math.abs(targetY - last.y) > 1) {
    vertices.push({ x: targetX, y: targetY });
  }
  return vertices;
};

const shiftPoint = (pt: { x: number; y: number }, dir: EdgeDirection, delta: number) => {
  switch (dir) {
    case 'top':
      return { x: pt.x, y: pt.y - delta };
    case 'bottom':
      return { x: pt.x, y: pt.y + delta };
    case 'left':
      return { x: pt.x - delta, y: pt.y };
    case 'right':
    default:
      return { x: pt.x + delta, y: pt.y };
  }
};

const isOptionArrowEdge = (edge: Edge) => {
  const sourcePort = (edge.getSource?.() as any)?.port;
  const data = (edge.getData?.() || {}) as EdgeRoutingMeta;
  return sourcePort === 'port-arrow' || !!data.isOptionArrowEdge;
};

export const applySingleEdgeRouting = (
  graph: Graph,
  edge: Edge,
  anchorName: EdgeDirection,
  isOptionTarget: boolean,
  forceOptionVertices = true,
) => {
  const data = ensureRouteMetadata(edge);
  const targetId = edge.getTargetCellId();
  const sourceId = edge.getSourceCellId();
  const isOptionEdge = isOptionArrowEdge(edge);
  const sourceNode = sourceId ? (graph.getCellById(sourceId) as Node | null) : null;
  const targetNode = targetId ? (graph.getCellById(targetId) as Node | null) : null;
  const optionApproachDir: EdgeDirection = isOptionEdge && sourceNode && targetNode
    ? pickDirectionFacingSource()
    : 'top';
  if (targetId) {
    const effectiveAnchor = isOptionEdge ? optionApproachDir : anchorName;
    const targetConfig: any = { cell: targetId, anchor: { name: effectiveAnchor } };
    if (isOptionEdge) {
      if (isOptionTarget) {
        targetConfig.port = 'port-arrow';
        targetConfig.connectionPoint = { name: 'anchor' };
      } else {
        targetConfig.port = `port-${effectiveAnchor}`;
        targetConfig.connectionPoint = { name: 'boundary' };
      }
    } else {
      targetConfig.port = `port-${anchorName}`;
      targetConfig.connectionPoint = { name: 'anchor' };
    }
    edge.setTarget(targetConfig);
  }
  const optionVertices = isOptionEdge && forceOptionVertices
    ? buildOptionArrowVertices(graph, edge, optionApproachDir)
    : null;
  const bottomVertices = !isOptionEdge && anchorName === 'bottom' && sourceNode && targetNode
    ? buildBottomEntryVertices(graph, edge, sourceNode, targetNode)
    : null;
  const customVertices = optionVertices || bottomVertices;
  if (customVertices && customVertices.length) {
    edge.setVertices(customVertices);
    edge.setRouter({ name: 'normal' });
    edge.setConnector('normal');
    if (optionVertices) {
      const lineAttrs = (edge.getAttrByPath?.('line') || {}) as Record<string, any>;
      const strokeWidth = typeof lineAttrs.strokeWidth === 'number' ? lineAttrs.strokeWidth : 1.8;
      edge.setAttrs({
        line: {
          ...lineAttrs,
          stroke: '#595959',
          strokeWidth,
          targetMarker: {
            name: 'path',
            args: {
              d: 'M 0 8 L 14 0 L 14 16 Z',
              fill: '#595959',
              stroke: '#595959',
              strokeWidth: 1.5,
            },
          },
        },
      });
    }
  } else {
    if (data._originalConnector) {
      edge.setConnector(data._originalConnector);
    }
    if (data._originalRouter) {
      edge.setRouter(data._originalRouter);
    }
  }
  edge.setData({
    ...data,
    _mergedEdge: true,
    ...(forceOptionVertices ? { isOptionArrowEdge: true } : {}),
  });
};

function ensureRouteMetadata(edge: Edge): EdgeRoutingMeta {
  const rawData = (edge.getData?.() || {}) as EdgeRoutingMeta;
  const data: EdgeRoutingMeta = { ...rawData };
  let changed = false;
  if (!data._originalConnector) {
    data._originalConnector = edge.getConnector?.();
    changed = true;
  }
  if (!data._originalRouter) {
    data._originalRouter = edge.getRouter?.();
    changed = true;
  }
  if (changed) {
    edge.setData(data);
  }
  return data;
}

/**
 * 合并同类型边：当多条同类型（edgeSubtype）的边指向同一个目标节点时，
 * 在目标节点附近计算一个汇聚点，让所有边先经过该汇聚点再到达目标，
 * 从而形成"合并展示"的视觉效果（树状/总线状）。
 *
 * 合并策略：
 * 1. 按 (targetId, edgeSubtype) 分组
 * 2. 组内只有1条边时不处理
 * 3. 组内多条边时，在目标节点外侧计算汇聚点，
 *    所有边设置两个顶点：各自的源端出射点 + 共享汇聚点
 */
export function mergeSameTypeEdges(graph: Graph): void {
  const edges = graph.getEdges();

  // 按 (targetId, edgeSubtype) 分组
  const groups = new Map<string, Edge[]>();

  edges.forEach(edge => {
    const targetId = edge.getTargetCellId();
    if (!targetId) return;

    const data = ensureRouteMetadata(edge);
    const category = data.edgeCategory || (edge.getData?.() as any)?.edgeCategory;
    if (category !== 'structural') return;
    const subtype = rawEdgeSubtype(edge, data);
    if (!subtype) return;

    if (isOptionArrowEdge(edge)) {
      const targetNode = graph.getCellById(targetId) as Node | null;
      const isOptionTarget = targetNode?.getData?.()?.type === 'option';
      applySingleEdgeRouting(graph, edge, 'top', !!isOptionTarget, true);
      return;
    }

    // 跳过同父子节点间的边及子节点与父副本之间的边，不做合并路由
    const sourceNode = graph.getCellById(edge.getSourceCellId()!) as Node | null;
    const targetNode = graph.getCellById(targetId) as Node | null;
    if (sourceNode && targetNode) {
      const srcParent = sourceNode.getParent?.();
      const tgtParent = targetNode.getParent?.();
      // 子节点之间的互连
      if (srcParent && tgtParent && srcParent.id === tgtParent.id && srcParent.isNode?.()) {
        layoutSiblingEdgeWithinParent(edge, sourceNode, targetNode, srcParent as Node);
        if (data._mergedEdge) edge.setData({ ...data, _mergedEdge: false });
        return;
      }
      // 子节点与父副本之间的互连
      const srcData = sourceNode.getData?.() || {};
      const tgtData = targetNode.getData?.() || {};
      if (srcParent?.isNode?.() && tgtData.isParentCopy && srcParent.id === targetNode.id) {
        // source 是 target(父副本) 的子节点
        layoutChildToParentEdgeWithinParent(edge, sourceNode, targetNode);
        if (data._mergedEdge) edge.setData({ ...data, _mergedEdge: false });
        return;
      }
      if (tgtParent?.isNode?.() && srcData.isParentCopy && tgtParent.id === sourceNode.id) {
        // target 是 source(父副本) 的子节点
        layoutChildToParentEdgeWithinParent(edge, targetNode, sourceNode);
        if (data._mergedEdge) edge.setData({ ...data, _mergedEdge: false });
        return;
      }
    }

    const key = `${targetId}::${subtype}`;
    const group = groups.get(key) || [];
    group.push(edge);
    groups.set(key, group);
  });

  groups.forEach(group => {
    if (!group.length) return;
    const optionArrowEdges = group.filter(isOptionArrowEdge);
    const remainingEdges = group.filter(edge => !isOptionArrowEdge(edge));

    const sampleEdge = (remainingEdges[0] || optionArrowEdges[0]);
    if (!sampleEdge) return;

    const targetNode = graph.getCellById(sampleEdge.getTargetCellId()!) as Node;
    if (!targetNode || !targetNode.isNode()) return;
    const isOptionTarget = targetNode.getData?.()?.type === 'option';

    optionArrowEdges.forEach(edge => {
      applySingleEdgeRouting(graph, edge, 'top', isOptionTarget);
    });

    if (!remainingEdges.length) {
      return;
    }

    const targetPos = targetNode.getPosition();
    const targetSize = targetNode.getSize();
    const targetCenter = {
      x: targetPos.x + targetSize.width / 2,
      y: targetPos.y + targetSize.height / 2,
    };

    // 计算所有源节点的中心，确定汇聚点方向
    const sourceCenters: { x: number; y: number }[] = [];
    remainingEdges.forEach(edge => {
      const sourceNode = graph.getCellById(edge.getSourceCellId()!) as Node;
      if (sourceNode && sourceNode.isNode()) {
        const sp = sourceNode.getPosition();
        const ss = sourceNode.getSize();
        sourceCenters.push({
          x: sp.x + ss.width / 2,
          y: sp.y + ss.height / 2,
        });
      }
    });

    if (sourceCenters.length === 0) return;

    // 计算源节点群的平均中心
    const avgSource = sourceCenters.reduce(
      (acc, c) => ({ x: acc.x + c.x, y: acc.y + c.y }),
      { x: 0, y: 0 },
    );
    avgSource.x /= sourceCenters.length;
    avgSource.y /= sourceCenters.length;

    const diffX = avgSource.x - targetCenter.x;
    const diffY = avgSource.y - targetCenter.y;

    const srcMinX = Math.min(...sourceCenters.map(c => c.x));
    const srcMaxX = Math.max(...sourceCenters.map(c => c.x));
    const srcMinY = Math.min(...sourceCenters.map(c => c.y));
    const srcMaxY = Math.max(...sourceCenters.map(c => c.y));
    const margin = 20;
    const targetOutsideVertical = targetCenter.y < srcMinY - margin || targetCenter.y > srcMaxY + margin;
    const targetOutsideHorizontal = targetCenter.x < srcMinX - margin || targetCenter.x > srcMaxX + margin;

    let useHorizontal: boolean;
    if (targetOutsideVertical && !targetOutsideHorizontal) {
      useHorizontal = false;
    } else if (targetOutsideHorizontal && !targetOutsideVertical) {
      useHorizontal = true;
    } else {
      useHorizontal = Math.abs(diffX) >= Math.abs(diffY);
    }
    const sourcePort = (sampleEdge.getSource?.() as any)?.port;
    const isOptionArrowSource = sourcePort === 'port-arrow';
    let anchorName: EdgeDirection = isOptionTarget ? 'top' : 'bottom';

    if (remainingEdges.length <= 1) {
      if (remainingEdges[0]) {
        const data = ensureRouteMetadata(remainingEdges[0]);
        remainingEdges[0].setData({ ...data, _mergedEdge: true });
        applySingleEdgeRouting(graph, remainingEdges[0], anchorName, isOptionTarget, false);
      }
      return;
    }

    // 汇聚点位于目标节点外侧（共享主干）
    const HUB_OFFSET = Math.max(40, useHorizontal ? targetSize.width / 2 + 20 : targetSize.height / 2 + 20);
    const mergePoint = useHorizontal
      ? {
          x: diffX > 0 ? targetPos.x + targetSize.width + HUB_OFFSET : targetPos.x - HUB_OFFSET,
          y: targetCenter.y,
        }
      : {
          x: targetCenter.x,
          y: diffY > 0 ? targetPos.y + targetSize.height + HUB_OFFSET : targetPos.y - HUB_OFFSET,
        };

    const targetBoundaryForAnchor = getBoundaryPoint(targetNode, anchorName);

    const approachTowardsTarget = (anchor: EdgeDirection) => {
      switch (anchor) {
        case 'top':
          return { x: targetBoundaryForAnchor.x, y: targetBoundaryForAnchor.y - 8 };
        case 'bottom':
          return { x: targetBoundaryForAnchor.x, y: targetBoundaryForAnchor.y + 8 };
        case 'left':
          return { x: targetBoundaryForAnchor.x - 8, y: targetBoundaryForAnchor.y };
        case 'right':
        default:
          return { x: targetBoundaryForAnchor.x + 8, y: targetBoundaryForAnchor.y };
      }
    };
    const targetApproachPoint = approachTowardsTarget(anchorName);

    remainingEdges.forEach(edge => {
      const data = ensureRouteMetadata(edge);
      const targetId = edge.getTargetCellId();
      if (targetId) {
        const targetConfig: any = { cell: targetId, anchor: { name: anchorName } };
        if (isOptionTarget || isOptionArrowSource) {
          targetConfig.port = isOptionTarget ? 'port-arrow' : 'port-top';
          targetConfig.connectionPoint = { name: 'anchor' };
        } else {
          targetConfig.port = 'port-bottom';
          targetConfig.connectionPoint = { name: 'anchor' };
        }
        edge.setTarget(targetConfig);
      }

      const sourceNode = graph.getCellById(edge.getSourceCellId()!) as Node;
      if (!sourceNode || !sourceNode.isNode()) return;

      const sp = sourceNode.getPosition();
      const ss = sourceNode.getSize();
      const sourceCenter = { x: sp.x + ss.width / 2, y: sp.y + ss.height / 2 };
      const hubPoint = { x: mergePoint.x, y: mergePoint.y };

      const desiredSourceDir: EdgeDirection = (() => {
        const deltaX = targetCenter.x - sourceCenter.x;
        const deltaY = targetCenter.y - sourceCenter.y;
        if (Math.abs(deltaX) < 6 && Math.abs(deltaY) < 6) return 'bottom';
        if (Math.abs(deltaX) >= Math.abs(deltaY)) {
          return deltaX >= 0 ? 'right' : 'left';
        }
        return deltaY >= 0 ? 'bottom' : 'top';
      })();

      const sourceConfig: any = { cell: sourceNode.id };
      const sourcePort = (edge.getSource?.() as any)?.port;
      if (sourcePort === 'port-arrow') {
        sourceConfig.port = sourcePort;
        sourceConfig.connectionPoint = { name: 'anchor' };
      } else {
        sourceConfig.port = `port-${desiredSourceDir}`;
        sourceConfig.connectionPoint = { name: 'anchor' };
      }
      sourceConfig.anchor = { name: desiredSourceDir };
      edge.setSource(sourceConfig);

      // 计算源节点边界点 + 固定下探/侧移距离，模仿 taxi 下探效果
      const boundaryPoint = getBoundaryPoint(sourceNode, desiredSourceDir);
      const elbowDistance = useHorizontal ? Math.max(30, Math.abs(sourceCenter.y - hubPoint.y) / 2) : Math.max(30, Math.abs(sourceCenter.x - hubPoint.x) / 2);
      const elbowPoint = shiftPoint(boundaryPoint, desiredSourceDir, elbowDistance);
      const approachPoint = useHorizontal
        ? { x: elbowPoint.x, y: hubPoint.y }
        : { x: hubPoint.x, y: elbowPoint.y };

      edge.setRouter({ name: 'normal' });
      edge.setConnector('normal');
      edge.setVertices([elbowPoint, approachPoint, hubPoint, targetApproachPoint]);

      if (!data._mergedEdge) {
        edge.setData({ ...data, _mergedEdge: true });
      }
    });
  });
}

function rawEdgeSubtype(edge: Edge, data: EdgeRoutingMeta): string {
  return (
    data.edgeSubtype ||
    ((edge.getData?.() as any)?.edgeSubtype as string) ||
    ''
  );
}

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const nodeCenter = (node: Node) => {
  const bbox = node.getBBox();
  return {
    x: bbox.x + bbox.width / 2,
    y: bbox.y + bbox.height / 2,
  };
};

const parentBoundsWithPadding = (parent: Node, padding: number) => {
  const bbox = parent.getBBox();
  return {
    minX: bbox.x + padding,
    maxX: bbox.x + bbox.width - padding,
    minY: bbox.y + padding,
    maxY: bbox.y + bbox.height - padding,
  };
};

const nudgePointInside = (point: { x: number; y: number }, dir: EdgeDirection, inset: number) => {
  switch (dir) {
    case 'left':
      return { x: point.x + inset, y: point.y };
    case 'right':
      return { x: point.x - inset, y: point.y };
    case 'top':
      return { x: point.x, y: point.y + inset };
    case 'bottom':
    default:
      return { x: point.x, y: point.y - inset };
  }
};

export function layoutSiblingEdgeWithinParent(
  edge: Edge,
  sourceNode: Node,
  targetNode: Node,
  parentNode: Node,
) {
  const bounds = parentBoundsWithPadding(parentNode, 24);
  const srcCenter = nodeCenter(sourceNode);
  const tgtCenter = nodeCenter(targetNode);
  const dx = tgtCenter.x - srcCenter.x;
  const dy = tgtCenter.y - srcCenter.y;
  const horizontalFirst = Math.abs(dx) >= Math.abs(dy);

  const startDir: EdgeDirection = horizontalFirst
    ? dx >= 0 ? 'right' : 'left'
    : dy >= 0 ? 'bottom' : 'top';
  const endDir: EdgeDirection = horizontalFirst
    ? dx >= 0 ? 'left' : 'right'
    : dy >= 0 ? 'top' : 'bottom';

  const startPoint = getBoundaryPoint(sourceNode, startDir);
  const endPoint = getBoundaryPoint(targetNode, endDir);

  const inset = 18;
  const startInside = nudgePointInside(startPoint, startDir, inset);
  const endInside = nudgePointInside(endPoint, endDir, inset);
  startInside.x = clamp(startInside.x, bounds.minX, bounds.maxX);
  startInside.y = clamp(startInside.y, bounds.minY, bounds.maxY);
  endInside.x = clamp(endInside.x, bounds.minX, bounds.maxX);
  endInside.y = clamp(endInside.y, bounds.minY, bounds.maxY);

  let vertices: { x: number; y: number }[];
  if (horizontalFirst) {
    const midX = clamp((startPoint.x + endPoint.x) / 2, bounds.minX, bounds.maxX);
    vertices = [
      startInside,
      { x: midX, y: startInside.y },
      { x: midX, y: endInside.y },
      endInside,
    ];
  } else {
    const midY = clamp((startPoint.y + endPoint.y) / 2, bounds.minY, bounds.maxY);
    vertices = [
      startInside,
      { x: startInside.x, y: midY },
      { x: endInside.x, y: midY },
      endInside,
    ];
  }

  edge.setVertices(vertices);
  edge.setRouter({ name: 'orth', args: { padding: 4 } });
  edge.setConnector('normal');

  const parentZ = parentNode.getZIndex?.();
  if (typeof parentZ === 'number' && edge.setZIndex) {
    edge.setZIndex(parentZ + 1);
  }
}

export function layoutChildToParentEdgeWithinParent(
  edge: Edge,
  childNode: Node,
  parentNode: Node,
) {
  const bounds = parentBoundsWithPadding(parentNode, 24);
  const childCenter = nodeCenter(childNode);
  const parentCenter = nodeCenter(parentNode);
  const dx = parentCenter.x - childCenter.x;
  const dy = parentCenter.y - childCenter.y;
  const horizontalFirst = Math.abs(dx) >= Math.abs(dy);

  const childDir: EdgeDirection = horizontalFirst
    ? dx >= 0 ? 'right' : 'left'
    : dy >= 0 ? 'bottom' : 'top';

  const childBoundary = getBoundaryPoint(childNode, childDir);
  const inset = 18;
  const childInside = nudgePointInside(childBoundary, childDir, inset);
  childInside.x = clamp(childInside.x, bounds.minX, bounds.maxX);
  childInside.y = clamp(childInside.y, bounds.minY, bounds.maxY);

  // 父节点端点：朝向子节点方向，在父节点内边界附近
  const parentDir: EdgeDirection = horizontalFirst
    ? dx >= 0 ? 'left' : 'right'
    : dy >= 0 ? 'top' : 'bottom';
  const parentBoundary = getBoundaryPoint(parentNode, parentDir);
  const parentInside = nudgePointInside(parentBoundary, parentDir, inset);
  parentInside.x = clamp(parentInside.x, bounds.minX, bounds.maxX);
  parentInside.y = clamp(parentInside.y, bounds.minY, bounds.maxY);

  let vertices: { x: number; y: number }[];
  if (horizontalFirst) {
    const midX = clamp((childInside.x + parentInside.x) / 2, bounds.minX, bounds.maxX);
    vertices = [
      childInside,
      { x: midX, y: childInside.y },
      { x: midX, y: parentInside.y },
      parentInside,
    ];
  } else {
    const midY = clamp((childInside.y + parentInside.y) / 2, bounds.minY, bounds.maxY);
    vertices = [
      childInside,
      { x: childInside.x, y: midY },
      { x: parentInside.x, y: midY },
      parentInside,
    ];
  }

  edge.setVertices(vertices);
  edge.setRouter({ name: 'orth', args: { padding: 4 } });
  edge.setConnector('normal');

  const parentZ = parentNode.getZIndex?.();
  if (typeof parentZ === 'number' && edge.setZIndex) {
    edge.setZIndex(parentZ + 1);
  }
}
